import React from 'react';
import { Box, Typography, Accordion, Slider, Loader } from '@lucid/lego';
import { useGetCustomerCaseById } from '../../../../../api/services/lupp/customer/customer';
import Comments from '../../../../../components/Comments/Comments';
import { formatCaseTitle } from '../utils';
import { formatDateTime } from '../../../../../utils/tools';
import styles from './Details.module.scss';

interface DetailsProps {
  theme: 'light' | 'dark';
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
}

const Details: React.FC<DetailsProps> = ({ theme, isOpen, onClose, caseId }) => {
  const { data: caseData, isLoading, error } = useGetCustomerCaseById(caseId, isOpen);
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  return (
    <Slider
      isOpen={isOpen && !!caseId}
      onClose={onClose}
      title={formatCaseTitle(caseData?.customerCaseIdentifier || '-')}
      width="1000px"
      direction="right"
      theme={invertedTheme}
    >
      {isLoading ? (
        <Box pt={48} pb={48} className="fetchingOverlay">
          <Loader size={32} />
        </Box>
      ) : error ? (
        <Box pt={48} pb={48}>
          <Typography variant="body" size="large" theme={invertedTheme} color="error-1">
            Error loading case details. Please try again.
          </Typography>
        </Box>
      ) : caseData ? (
        <Box className={styles.detailsContainer}>
          {/* Case Overview */}
          <Accordion
            title="Case Overview"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.detailsContainer}>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Case Number
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {formatCaseTitle(caseData?.customerCaseIdentifier || '-')}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Subject
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {caseData?.caseSubject || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.innerBox}>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Description
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {caseData?.caseSummary || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.gridView}>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Status
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.status || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Priority
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.priority || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Accordion>

          {/* Customer Information */}
          <Accordion
            title="Customer Information"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.detailsContainer}>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Customer Name
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {caseData?.customerName || '-'}
                  </Typography>
                </Box>
              </Box>
              <Box className={styles.gridView}>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Email
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.customerEmail || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Phone
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.customerPhone || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Address
                </Typography>
                <Box>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    {caseData?.locationOfEvent || '-'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Accordion>

          {/* Vehicle Information */}
          {caseData?.vin && (
            <Accordion
              title="Vehicle Information"
              theme={invertedTheme}
              defaultOpen={true}
              showBorder={true}
            >
              <Box className={styles.gridView}>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    VIN
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.vin || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Model
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Year
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Colour
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Mileage
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Delivery Date
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Accordion>
          )}

          {/* Case Management */}
          <Accordion
            title="Case Management"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.detailsContainer}>
              <Box className={styles.gridView}>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Assigned Agent
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      -
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Case Type
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {caseData?.caseParentCategory || '-'}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Estimated Resolution
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      - {/* Jan 21, 2024, 03:30 PM */}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Created Date
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {formatDateTime(caseData?.createdDatetime)}
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.innerBox}>
                  <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                    Last Updated
                  </Typography>
                  <Box>
                    <Typography variant="body" size="small" theme={invertedTheme} color="white">
                      {formatDateTime(caseData?.updatedDatetime)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography variant="label" size="small" theme={invertedTheme} color="primary-1">
                  Tags
                </Typography>
                <Box className={styles.tagsContainer}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    -
                  </Typography>
                  {/* {caseDetail.caseDetails.tags.map((tag, index) => (
                  <Badge key={`tag-${index}`} label={tag} color="informative" theme={invertedTheme} />
                ))} */}
                  {/* <Badge label={'Battery'} color="informative" theme={invertedTheme} />
                <Badge label={'Range'} color="informative" theme={invertedTheme} />
                <Badge label={'High Priority'} color="informative" theme={invertedTheme} /> */}
                </Box>
              </Box>
            </Box>
          </Accordion>

          {/* Comments Section */}
          <Accordion title="Comments" theme={invertedTheme} defaultOpen={true} showBorder={true}>
            <Comments
              entityId={caseId}
              entityType="CUSTOMER_CASE"
              theme={invertedTheme}
              enabled={isOpen}
              gaComponent="case-detail-post-comment"
            />
          </Accordion>

          {/* Activity Timeline */}
          <Accordion
            title="Activity Timeline"
            theme={invertedTheme}
            defaultOpen={true}
            showBorder={true}
          >
            <Box className={styles.timelineContainer}>
              {caseData?.caseStatusLogs?.map((log, index) => (
                <Box key={`caselog-${index}`} className={styles.timelineEntry}>
                  <Typography variant="body" size="small" theme={invertedTheme} color="primary-1">
                    {formatDateTime(log.startDatetime)}
                  </Typography>
                  <Typography variant="body" size="small" theme={invertedTheme} color="white">
                    Case created
                  </Typography>
                  <Typography variant="body" size="small" theme={invertedTheme} color="gray-5">
                    by {log.createdBy || 'System'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Accordion>
        </Box>
      ) : (
        <Box pt={48} pb={48}>
          <Typography variant="body" size="medium" theme={invertedTheme} color="white">
            No case details available
          </Typography>
        </Box>
      )}
    </Slider>
  );
};

export default Details;
